import { useApp } from '../common/AppProvider';
import { Action, Connector, Trigger } from './types';
import { stringReplace } from '../common/utils';
import { useState } from 'react';
import { translations } from '../translations/translations';
import { TaskEditAction } from './Task';
import Selector from '../common/Selector';
import { ExButton, ExIcon } from '@boomi/exosphere';
import SimpleSlider from '../common/SimpleSlider';

type TaskItemType = 'action' | 'trigger';
type View = 'prompt' | 'connector' | 'item';
type TaskItemOption = { label: string; value: Action | Trigger };

interface Props {
    index?: number;
    itemType: TaskItemType;
    dispatchTask: React.Dispatch<TaskEditAction>;
}

const AddTaskItem = ({ dispatchTask, itemType, index }: Props) => {
    const { connectors, language } = useApp();

    const [view, setView] = useState<View>(itemType === 'trigger' ? 'connector' : 'prompt');
    const [connector, setConnector] = useState<Connector | null>(null);

    const onAddTaskItem = () => {
        setConnector(null);
        setView('connector');
    };

    const onConnectorChange = (option: Connector | null) => {
        setConnector(option);
    };

    const onActionChange = (option: TaskItemOption | null) => {
        if (connector && option) {
            if (itemType === 'trigger') {
                dispatchTask({
                    type: 'addTrigger',
                    payload: { trigger: option.value as Trigger },
                });
            } else {
                dispatchTask({
                    type: 'addAction',
                    payload: { action: option.value as Action, index: index as number },
                });
            }

            setView('prompt');
        }
    };

    const translatedItemType = translations[language][`TASK_item_type_${itemType}`];

    switch (view) {
        case 'prompt': {
            return (
                <div className="task-item-add">
                    <ExButton
                        data-testid="addActionButton"
                        className={`add-${itemType} exo-button`}
                        flavor="base"
                        type="primary"
                        size="large"
                        onClick={onAddTaskItem}
                    >
                        <ExIcon icon="plus" slot="prefix" hide-browser-tooltip />
                        {stringReplace(
                            translations[language].TASK_add_task_item,
                            translatedItemType,
                        )}
                    </ExButton>
                </div>
            );
        }

        case 'connector': {
            return (
                <div className="task-item new-connector ta-tile">
                    <div className="task-item-title">
                        <p className="text md">
                            {itemType === 'trigger'
                                ? translations[language].TASK_when_hint
                                : translations[language].TASK_then_hint}
                        </p>
                        <ExButton
                            data-testid="deleteActionButton"
                            className="delete-action exo-button-action"
                            flavor="risky"
                            size="large"
                            type="tertiary"
                            onClick={() => setView('prompt')}
                        >
                            <ExIcon icon="trash" hide-browser-tooltip />
                        </ExButton>
                    </div>

                    <section className="simple-slider-connectors">
                        <SimpleSlider
                            sliderItems={connectors}
                            itemType={itemType}
                            isMultiSelect={false}
                            selectedItems={connector ? [connector] : []}
                            onSelect={onConnectorChange}
                            language={language}
                            showCount={false}
                        />
                    </section>

                    {connector && itemType && (
                        <Selector
                            connector={connector}
                            itemType={itemType}
                            onSelect={onActionChange}
                            language={language}
                        />
                    )}
                </div>
            );
        }
    }
};

export default AddTaskItem;
